import React, { useContext } from "react";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";
import { MagicBellProvider } from "@magicbell/magicbell-react";

import { ReactComponent as LogoutIcon } from "assets/images/icons/Logout.svg";
import { ReactComponent as SettingsIcon } from "assets/images/icons/Settings.svg";
import testIds from "constants/testIds";
import { IItemProps } from "components/molecules/Navigation/interface/sidebar";
import { UserType } from "types/UserTypes";
import usePermissions from "hooks/usePermissions";
import Notifications from "components/organisms/Notifications/Notifications";
import Cookies from "js-cookie";
import MenuItem from "./MenuItem";
import ExpandMenu from "./ExpandMenu";
import NavContext from "./NavContext";
import ShipperMenuItems from "../shipper/ShipperMenuItems";
import { MagicBellTheme } from "../../../../../constants";
import ProviderMenuItems from "../provider/ProviderMenuItems";
import GuestUserMenuItems from "./GuestUserMenuItems";

interface MenuItemsProps {
  setIsExpanded: Function;
  userType: UserType;
  isStaff: boolean;
}

const MenuItems = function ({ setIsExpanded, userType, isStaff }: MenuItemsProps) {
  const intl = useIntl();
  const { navigationConfig, setNavigationConfig } = useContext(NavContext);
  const { permissions } = usePermissions();
  const { email } = JSON.parse(permissions || "{}");

  const handleCloseNav = () => {
    if (navigationConfig.isMobile)
      setNavigationConfig({
        ...navigationConfig,
        isShown: false,
        isMobile: true,
        expanded: false,
      });
    else if (!navigationConfig.isMobile)
      setNavigationConfig({
        ...navigationConfig,
        expanded: false,
      });
    navigationConfig.handleNavigation(navigationConfig.expanded);
  };

  const navigationBottom: IItemProps[] = [
    {
      eventKey: "8",
      id: "view_settings",
      icon: <SettingsIcon />,
      label: intl.formatMessage({
        id: "components.molecules.navigation.settings",
        defaultMessage: "Settings",
      }),
      parentUrl: "/settings",
      to: "/settings/your-details",
      enabled: Cookies.get("is_guest") !== "true",
      testId: testIds.MENU_SETTINGS_BUTTON,
    },
    {
      eventKey: "9",
      id: "logout",
      icon: <LogoutIcon />,
      to: "/logout",
      parentUrl: "/logout",
      label: intl.formatMessage({
        id: "components.molecules.navigation.logout",
        defaultMessage: "Log out",
      }),
      enabled: true,
      testId: testIds.MENU_LOGOUT_BUTTON,
    },
  ];

  function getMenuComponent() {
    switch (userType) {
      case "shipper":
        return <ShipperMenuItems handleCloseNav={handleCloseNav} />;
      case "provider":
        return <ProviderMenuItems handleCloseNav={handleCloseNav} />;
      default:
        return <GuestUserMenuItems handleCloseNav={handleCloseNav} />; // if userType does not exist the user is a guest
    }
  }

  return (
    <ul className="d-flex flex-column align-items-start justify-content-between h-100 mb-2">
      {getMenuComponent()}
      <div className="menu-item-center w-100">
        <ExpandMenu setIsExpanded={setIsExpanded} />
        {/* currently making magicbell notification service only available for QA env */}
        {email &&
          userType === "shipper" &&
          import.meta.env.REACT_APP_ENVIRONMENT_NAME === "qa" &&
          Cookies.get("is_guest") !== "true" && (
            <MagicBellProvider
              apiKey={import.meta.env.REACT_APP_MAGIC_BELL_API_KEY as string}
              userEmail={email}
              theme={MagicBellTheme}
            >
              <Notifications isStaff={isStaff} />
            </MagicBellProvider>
          )}
        {navigationBottom.map((item) => (
          <MenuItem item={item} key={item.id} closeNav={handleCloseNav} testId={item.testId} />
        ))}
      </div>
    </ul>
  );
};

MenuItems.propTypes = {
  setIsExpanded: PropTypes.func.isRequired,
};

export default MenuItems;
